import { useCallback, useEffect, useRef, useState } from "react";

export type FieldPilotLayerId = "measurements" | "tags";

export interface FieldPilotLayerVisibility {
  measurements: boolean;
  tags: boolean;
}

export const DEFAULT_FIELD_PILOT_LAYER_VISIBILITY: FieldPilotLayerVisibility = {
  measurements: true,
  tags: false,
};

export const FIELD_PILOT_LAYERS_STORAGE_PREFIX = "field-pilot-layers:v1";

export function fieldPilotLayersStorageKey(jobId: string) {
  return `${FIELD_PILOT_LAYERS_STORAGE_PREFIX}:${jobId}`;
}

export function parseFieldPilotLayerVisibility(
  raw: string | null
): FieldPilotLayerVisibility {
  if (!raw) {
    return { ...DEFAULT_FIELD_PILOT_LAYER_VISIBILITY };
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      typeof (parsed as FieldPilotLayerVisibility).measurements !== "boolean" ||
      typeof (parsed as FieldPilotLayerVisibility).tags !== "boolean"
    ) {
      return { ...DEFAULT_FIELD_PILOT_LAYER_VISIBILITY };
    }
    return {
      measurements: (parsed as FieldPilotLayerVisibility).measurements,
      tags: (parsed as FieldPilotLayerVisibility).tags,
    };
  } catch {
    return { ...DEFAULT_FIELD_PILOT_LAYER_VISIBILITY };
  }
}

export function readFieldPilotLayerVisibility(jobId: string): FieldPilotLayerVisibility {
  if (typeof window === "undefined") {
    return { ...DEFAULT_FIELD_PILOT_LAYER_VISIBILITY };
  }
  try {
    return parseFieldPilotLayerVisibility(
      window.localStorage.getItem(fieldPilotLayersStorageKey(jobId))
    );
  } catch {
    return { ...DEFAULT_FIELD_PILOT_LAYER_VISIBILITY };
  }
}

function writeFieldPilotLayerVisibility(
  jobId: string,
  visibility: FieldPilotLayerVisibility
) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      fieldPilotLayersStorageKey(jobId),
      JSON.stringify(visibility)
    );
  } catch {
    // Ignore quota / private-mode failures; in-memory state still works.
  }
}

export interface UseFieldPilotLayersArgs {
  jobId: string;
  onHideMeasurements?: () => void;
}

export function useFieldPilotLayers({
  jobId,
  onHideMeasurements,
}: UseFieldPilotLayersArgs) {
  const [visibility, setVisibility] = useState<FieldPilotLayerVisibility>(() =>
    readFieldPilotLayerVisibility(jobId)
  );
  const visibilityRef = useRef(visibility);
  visibilityRef.current = visibility;
  const onHideMeasurementsRef = useRef(onHideMeasurements);
  onHideMeasurementsRef.current = onHideMeasurements;

  useEffect(() => {
    const next = readFieldPilotLayerVisibility(jobId);
    visibilityRef.current = next;
    setVisibility(next);
    if (!next.measurements) {
      onHideMeasurementsRef.current?.();
    }
  }, [jobId]);

  const setLayerVisible = useCallback(
    (layerId: FieldPilotLayerId, visible: boolean) => {
      const current = visibilityRef.current;
      if (current[layerId] === visible) {
        return;
      }
      const next = { ...current, [layerId]: visible };
      visibilityRef.current = next;
      setVisibility(next);
      if (layerId === "measurements" && !visible) {
        onHideMeasurementsRef.current?.();
      }
      writeFieldPilotLayerVisibility(jobId, next);
    },
    [jobId]
  );

  const enableMeasurements = useCallback(() => {
    setLayerVisible("measurements", true);
  }, [setLayerVisible]);

  const enableTags = useCallback(() => {
    setLayerVisible("tags", true);
  }, [setLayerVisible]);

  return {
    visibility,
    setLayerVisible,
    enableMeasurements,
    enableTags,
  };
}
